import type { Position } from '../../types';
import type { PlayerRuntimeState } from '../match/PlayerRuntimeState';
import { PITCH_GEOMETRY } from '../match/PitchGeometry';
import { clamp, type Vector2 } from '../match/Vector2';
import { ROLE_DEFINITIONS, validateAssignedRole } from './TacticalRoleSystem';

export interface RoleConstraint {
  /** Preferred longitudinal band used by telemetry, not a hard movement wall. */
  homeZone: [number, number];
  /** Diagnostic safety envelope. Outfield targets are governed by anchors. */
  maxAdvance: number;
  maxDrop: number;
}

export const ROLE_CONSTRAINTS: Record<Position, RoleConstraint> = {
  // A goalkeeper can sweep a loose ball near their own area, but should never
  // become another outfield runner. Keeping this corridor tight also prevents
  // their marker/avoidance movement from carrying them out of the goalmouth.
  GK: { homeZone: [2, PITCH_GEOMETRY.goalkeeper.normalMaxProgress], maxAdvance: PITCH_GEOMETRY.goalkeeper.normalMaxProgress, maxDrop: PITCH_GEOMETRY.goalkeeper.normalMinProgress },
  DF: { homeZone: [12, 38], maxAdvance: 58, maxDrop: 5 },
  MF: { homeZone: [30, 68], maxAdvance: 86, maxDrop: 15 },
  FW: { homeZone: [58, 92], maxAdvance: 99, maxDrop: 38 },
};

export class RoleConstraints {
  constrain(player: PlayerRuntimeState, target: Vector2): Vector2 {
    const rule = ROLE_CONSTRAINTS[player.positionType];
    const emergency = ['CHASING_LOOSE_BALL', 'TRACKING_RUN'].includes(player.movementState);

    if (player.positionType === 'GK') {
      const maySweep = emergency && this.isNearOwnPenaltyArea(player, target);
      const maximum = maySweep ? PITCH_GEOMETRY.goalkeeper.sweepMaxProgress : PITCH_GEOMETRY.goalkeeper.normalMaxProgress;
      return {
        x: PITCH_GEOMETRY.worldX(player.teamSide, clamp(
          PITCH_GEOMETRY.relativeProgress(player.teamSide, target.x),
          rule.maxDrop,
          maximum,
        )),
        y: clamp(target.y, PITCH_GEOMETRY.goalkeeper.minY, PITCH_GEOMETRY.goalkeeper.maxY),
      };
    }

    const role = player.tacticalProfile.assignedRole;
    if (!validateAssignedRole(player.positionType, role) || !role) return PITCH_GEOMETRY.clampRestart(target);
    const tactical = ROLE_DEFINITIONS[role].constraints;
    const mayOverride = emergency && tactical.emergencyOverride;
    const progress = PITCH_GEOMETRY.relativeProgress(player.teamSide, target.x);
    const constrainedProgress = mayOverride ? progress : clamp(progress, tactical.maxDrop, tactical.maxAdvance);
    return PITCH_GEOMETRY.clampRestart({
      x: PITCH_GEOMETRY.worldX(player.teamSide, constrainedProgress),
      y: target.y,
    });
  }

  private isNearOwnPenaltyArea(player: PlayerRuntimeState, target: Vector2): boolean {
    return PITCH_GEOMETRY.relativeProgress(player.teamSide, target.x) <= PITCH_GEOMETRY.goalkeeper.emergencyAreaProgress;
  }
}
